/**
 * Standings-over-time chart block for LeaguePage (small private leagues only).
 *
 * Fetches leagues/{id}/history/* and member names, then renders StandingsChartPanel.
 * Returns null when ineligible or when no settled matchdays exist yet.
 */
import { type ReactNode } from 'react';
import { Box, Center, Spinner, Text } from '@chakra-ui/react';

import { StandingsChartPanel } from '@/components/standingsChart/StandingsChartPanel';
import type League from '@/models/league';
import { pageSectionTitleProps } from '@/theme/sitePageTypography';
import { Card } from '@/views/LeaguePage/Card';
import { useLeagueStandingsChartData } from './useLeagueStandingsChartData';

export interface LeagueStandingsChartSectionProps {
  league: League & { id: string };
}

function ChartCardShell(props: { title?: boolean; children: ReactNode }) {
  return (
    <Card w='full' borderRadius='md' p={{ base: 3, md: 4 }}>
      {props.title !== false ? (
        <Text {...pageSectionTitleProps} mb={2}>
          Points over time
        </Text>
      ) : null}
      {props.children}
    </Card>
  );
}

export function LeagueStandingsChartSection({ league }: LeagueStandingsChartSectionProps) {
  const { columns, standings, loading, error, ready, currentUid, gameResults } =
    useLeagueStandingsChartData(league);

  if (loading) {
    return (
      <ChartCardShell title={false}>
        <Center py={8} minH='200px'>
          <Spinner size='sm' />
        </Center>
      </ChartCardShell>
    );
  }

  if (error) {
    return (
      <ChartCardShell>
        <Text fontSize='sm' color='gray.500'>
          Could not load standings history.
        </Text>
      </ChartCardShell>
    );
  }

  if (!ready) {
    return null;
  }

  return (
    <Box id='standings-chart' w='full'>
      <ChartCardShell>
        <StandingsChartPanel
          embedded
          series={standings.series}
          maxMatchday={standings.maxMatchday}
          columns={columns}
          currentUid={currentUid}
          gameResults={gameResults}
        />
      </ChartCardShell>
    </Box>
  );
}
